import { motion } from "framer-motion";
import { ChevronRight, Inbox } from "lucide-react";

import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import { cn } from "../../../../lib/utils";
import { useClaimMotion } from "./claimMotion";
import { STATUS_TONES } from "./claimStatus";

/**
 * The card kit shared by the payments, documents and offers lists.
 *
 * All three were previously the same 40 lines of JSX pasted three times, which
 * is why they had drifted: one used a different grey for its disabled state,
 * only one had a hover style, and none of them was reachable by keyboard even
 * though tapping a row navigates.
 */

export const StatusPill = ({ tone = "neutral", children, className }) => (
  <span
    className={cn(
      "inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase leading-[18px] tracking-[0.04em]",
      STATUS_TONES[tone] || STATUS_TONES.neutral,
      className
    )}
  >
    {children}
  </span>
);

/**
 * One row: status, what it is, when it happened, and — when the row leads
 * somewhere — a chevron.
 *
 * Renders as a real `<button>` when actionable so it is tab-reachable and
 * fires on Enter/Space, and as a plain `<div>` when it is not, so a settled
 * payment is not announced to a screen reader as something to press.
 */
export const ClaimListCard = ({ status, title, meta, onSelect }) => {
  const translate = useLanguageTranslate();
  const motionPresets = useClaimMotion();
  const actionable = Boolean(status?.actionable && onSelect);
  const Element = actionable ? motion.button : motion.div;

  return (
    <Element
      {...motionPresets.listItem}
      {...(actionable ? motionPresets.tappable : {})}
      {...(actionable
        ? { type: "button", onClick: onSelect }
        : { "aria-disabled": true })}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl border border-[#DDE2E4] p-4 text-start",
        "transition-colors duration-150",
        actionable
          ? "cursor-pointer bg-white shadow-[0_1px_2px_rgba(26,32,36,0.04)] hover:border-[#B8C4CB] hover:bg-[#FBFCFD] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A9E7] focus-visible:ring-offset-2"
          : "bg-[#F6F8F9]"
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
        <StatusPill tone={status?.tone}>
          {translate(status?.label_en, status?.label_ar)}
        </StatusPill>

        {/* Wraps rather than truncates: a document name is the only thing that
            tells the customer which upload is still outstanding. */}
        <span
          className={cn(
            "text-[15px] font-semibold leading-[22px] break-words",
            actionable ? "text-[#1A2024]" : "text-[#48535B]"
          )}
        >
          {title}
        </span>

        {meta && (
          <span className="text-xs font-normal leading-4 text-[#6E7C87]">
            {meta}
          </span>
        )}
      </span>

      {actionable && (
        <ChevronRight
          className="size-4 shrink-0 text-[#9AA6AC] rtl:rotate-180"
          aria-hidden="true"
        />
      )}
    </Element>
  );
};

/**
 * A titled group of cards. The count sits next to the heading so the customer
 * can see there are three outstanding documents without counting the cards, and
 * the empty state says which list is empty rather than leaving a bare heading
 * over nothing — the old page's most common look, since most claims have no
 * offers.
 */
export const ClaimSection = ({
  title,
  count,
  emptyLabel,
  children,
  headingHidden = false,
  className,
}) => {
  const motionPresets = useClaimMotion();
  const isEmpty = !count;

  return (
    <section className={cn("flex flex-col gap-3", className)}>
      {!headingHidden && (
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold leading-6 tracking-[-0.16px] text-[#1A2024]">
            {title}
          </h2>
          {count > 0 && (
            <span className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-[#EDF1F3] px-1.5 text-[11px] font-semibold leading-5 text-[#5B6871]">
              {count}
            </span>
          )}
        </div>
      )}

      {isEmpty ? (
        <div className="flex items-center gap-2.5 rounded-xl border border-dashed border-[#DDE2E4] bg-[#FBFCFD] px-4 py-5 text-sm text-[#6E7C87]">
          <Inbox className="size-4 shrink-0" aria-hidden="true" />
          {emptyLabel}
        </div>
      ) : (
        <motion.div
          {...motionPresets.listContainer}
          className="flex flex-col gap-3"
        >
          {children}
        </motion.div>
      )}
    </section>
  );
};

export default ClaimListCard;
